import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, KeyRound } from "lucide-react";
import { API } from "../data/site";

const inputCls = "w-full rounded-xl border border-forest/20 bg-white px-4 py-2.5 text-sm text-ink outline-none focus:border-forest";

export const TeamModal = ({ details, token, onClose, onChanged }) => {
    const [name, setName] = useState("");
    const [pins, setPins] = useState({});
    const [busy, setBusy] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    const addWorker = async () => {
        const n = name.trim();
        if (!n) return;
        setBusy(true);
        try {
            await axios.post(`${API}/team`, { name: n }, { headers });
            toast.success(`${n} added to the team.`);
            setName("");
            onChanged();
        } catch {
            toast.error("Could not add team member.");
        } finally {
            setBusy(false);
        }
    };

    const savePin = async (workerName) => {
        const pin = (pins[workerName] || "").trim();
        if (!pin) { toast.error("Enter a PIN first."); return; }
        try {
            await axios.patch(`${API}/team/${encodeURIComponent(workerName)}`, { pin }, { headers });
            toast.success(`PIN set for ${workerName}.`);
            setPins({ ...pins, [workerName]: "" });
            onChanged();
        } catch {
            toast.error("Could not set PIN.");
        }
    };

    return (
        <div data-testid="team-modal" className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-forest-night/60 p-4 backdrop-blur-sm">
            <div className="my-8 w-full max-w-md rounded-3xl bg-bone-card p-6 shadow-2xl sm:p-8">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl font-bold tracking-tight text-ink">Team &amp; PINs</h2>
                    <button onClick={onClose} data-testid="team-modal-close" aria-label="Close"
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-forest/15 text-forest hover:bg-bone-subtle">
                        <X size={16} />
                    </button>
                </div>
                <p className="mt-2 text-sm text-moss">
                    Team members log in with their name and PIN for view-only access to the Job Scheduler.
                </p>
                <div className="mt-5 space-y-4">
                    {details.length === 0 && (
                        <p className="rounded-2xl border border-dashed border-forest/25 p-5 text-center text-sm text-moss">
                            No team members yet — add one below.
                        </p>
                    )}
                    {details.map((m) => (
                        <div key={m.name} data-testid={`team-member-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="rounded-2xl border border-forest/10 bg-bone p-4">
                            <p className="text-sm font-bold text-ink">
                                {m.name}
                                <span className={`ml-2 rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${m.has_pin ? "bg-forest/10 text-forest-leaf" : "bg-ochre/25 text-clay"}`}>
                                    {m.has_pin ? "PIN set" : "No PIN"}
                                </span>
                            </p>
                            <div className="mt-2.5 flex gap-2">
                                <input type="password" placeholder={m.has_pin ? "New PIN" : "Set a PIN"} value={pins[m.name] || ""}
                                    data-testid={`team-pin-input-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    onChange={(e) => setPins({ ...pins, [m.name]: e.target.value })} className={inputCls} />
                                <button onClick={() => savePin(m.name)} data-testid={`team-pin-save-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="flex shrink-0 items-center gap-1.5 rounded-xl bg-forest px-4 py-2 text-xs font-bold text-bone hover:bg-forest-deep">
                                    <KeyRound size={12} /> Save
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-5 flex gap-2">
                    <input placeholder="New team member name" value={name} onChange={(e) => setName(e.target.value)}
                        data-testid="team-new-name-input"
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addWorker())} className={inputCls} />
                    <button onClick={addWorker} disabled={busy} data-testid="team-add-button"
                        className="flex shrink-0 items-center gap-1.5 rounded-xl bg-forest px-4 py-2 text-xs font-bold text-bone hover:bg-forest-deep disabled:opacity-60">
                        <Plus size={13} /> Add
                    </button>
                </div>
            </div>
        </div>
    );
};
